/**
 * Client-side validation for the /join profile wizard.
 *
 * The API requires `company` (experience) and `school` (education) to be
 * non-empty. New rows are seeded blank when a user taps "Add", so we clean and
 * validate before submitting: fully-empty rows are dropped (accidental taps),
 * and a row that has content but is missing its required field blocks the save
 * with a human message instead of letting the server reject it with raw Zod.
 */

import type { EducationItem, ExperienceItem } from "./profile-types";

const isBlank = (value: string | null | undefined): boolean =>
  !value || value.trim().length === 0;

export const isExperienceEmpty = (e: ExperienceItem): boolean =>
  isBlank(e.company) &&
  // A row carrying only a website is not empty. Omitting this dropped it
  // silently, and the "add a company name" prompt below never fired because
  // the row had already been filtered out.
  isBlank(e.companyDomain) &&
  isBlank(e.role) &&
  isBlank(e.startedAt) &&
  isBlank(e.endedAt) &&
  isBlank(e.city) &&
  isBlank(e.country) &&
  isBlank(e.description);

export const isEducationEmpty = (e: EducationItem): boolean =>
  isBlank(e.school) &&
  isBlank(e.degree) &&
  isBlank(e.major) &&
  isBlank(e.startedAt) &&
  isBlank(e.endedAt) &&
  isBlank(e.gpa);

export interface CleanedProfileRows {
  experiences: ExperienceItem[];
  educations: EducationItem[];
  /** Human-readable blocker, or null when the rows are safe to submit. */
  error: string | null;
}

export const EXPERIENCE_MISSING_COMPANY_MESSAGE =
  "Add a company name to each work experience, or remove the empty one.";

export const EDUCATION_MISSING_SCHOOL_MESSAGE =
  "Add a school name to each education entry, or remove the empty one.";

/**
 * Drops fully-empty rows and reports the first blocking validation error.
 * Returned `experiences`/`educations` are always the cleaned arrays, safe to
 * submit only when `error` is null.
 */
export function cleanAndValidateProfileRows(
  experiences: ExperienceItem[],
  educations: EducationItem[],
): CleanedProfileRows {
  const cleanedExperiences = experiences.filter((e) => !isExperienceEmpty(e));
  const cleanedEducations = educations.filter((e) => !isEducationEmpty(e));

  if (cleanedExperiences.some((e) => isBlank(e.company))) {
    return {
      experiences: cleanedExperiences,
      educations: cleanedEducations,
      error: EXPERIENCE_MISSING_COMPANY_MESSAGE,
    };
  }

  if (cleanedEducations.some((e) => isBlank(e.school))) {
    return {
      experiences: cleanedExperiences,
      educations: cleanedEducations,
      error: EDUCATION_MISSING_SCHOOL_MESSAGE,
    };
  }

  return {
    experiences: cleanedExperiences,
    educations: cleanedEducations,
    error: null,
  };
}

/**
 * Maps a profile-save mutation error to a human message. tRPC input-validation
 * failures arrive as a stringified array of Zod issues; we parse it so a raw
 * JSON array can never reach the toast. Non-validation messages pass through.
 */
export function humanizeProfileSaveError(message: string): string {
  let issues: unknown;
  try {
    issues = JSON.parse(message);
  } catch {
    return message;
  }

  if (!Array.isArray(issues) || issues.length === 0) {
    return message;
  }

  const firstPath = (issues[0] as { path?: unknown })?.path;
  const topLevelField = Array.isArray(firstPath) ? firstPath[0] : undefined;

  if (topLevelField === "experiences") {
    return EXPERIENCE_MISSING_COMPANY_MESSAGE;
  }
  if (topLevelField === "educations") {
    return EDUCATION_MISSING_SCHOOL_MESSAGE;
  }
  return "Please complete the required fields before continuing.";
}
